import { Component, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { DynamicFilterConfig, ModalService, createDynamicFilterState } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { PaginatorState } from 'primeng/paginator';
import { SortEvent } from 'primeng/api';
import _ from 'lodash';
import { ExpenseConcept, ExpenseConceptSearchCommand } from '../../models/expense-concept/expense-concept';
import { ExpenseConceptProxyService } from '../../proxies/expense-concept.proxy.service';
import { LocaleHelperService } from '../../services/locale-helper.service';
import { UserAuthenticationService } from '../../services/user-authentication.service';
import { ExpenseConceptRoles } from '../../models/roles/roles-group';
import { ExpenseConceptModalComponent } from './_modals/expense-concept-modal/expense-concept-modal.component';

/** The filter panel's controls; select values arrive as strings ('true' / 'false'). */
interface ExpenseConceptFilterFormModel {
  search: string | null;
  isActive: string | null;
}

/** What the panel emits on apply — a persisted filter set may omit either field. */
type ExpenseConceptFilters = Partial<ExpenseConceptFilterFormModel>;

/**
 * Expense concepts (#5781) — the catalogue GL account rules and receipt lines key on by code. Codes are
 * immutable, so the modal edits only the name and the active flag; the API refuses to deactivate a concept
 * an active GL account rule still uses.
 */
@Component({
  selector: 'app-expense-concepts',
  templateUrl: './expense-concepts.component.html',
  styleUrl: './expense-concepts.component.scss',
})
export class ExpenseConceptsComponent {
  private readonly proxy = inject(ExpenseConceptProxyService);
  private readonly modalService = inject(ModalService);
  private readonly translateService = inject(TranslateService);
  private readonly locale = inject(LocaleHelperService);
  private readonly userAuth = inject(UserAuthenticationService);

  private currentSearchCriteria: ExpenseConceptSearchCommand = {};

  /** Viewers see the list only; the API refuses create/update without the admin role as well. */
  readonly canEdit = this.userAuth.roles.includes(ExpenseConceptRoles.ExpenseConceptAdmin);

  readonly isLoading = signal(0);
  readonly filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'expense-concept-filters', config: this.filterConfig });
  readonly items = signal<ExpenseConcept[]>([]);
  readonly totalRecords = signal(0);
  readonly currentPage = signal(1);
  readonly pageSize = signal(25);
  readonly sortField = signal<string | null>(null);
  readonly sortOrder = signal(1);

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters as ExpenseConceptFilters));

    effect(
      () => {
        if (this.translateService.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private t(key: string, fallback: string): string {
    const value = this.translateService.instant(key);
    return value === key ? fallback : value;
  }

  private tryInitializeFilterConfig(): void {
    this.filterConfig.set(this.buildFilterConfig());
  }

  private buildFilterConfig(): DynamicFilterConfig {
    return {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'search',
          label: this.t('ExpenseConcept_Filter_Search', 'Code or name'),
          placeholder: this.t('ExpenseConcept_Filter_Search_Placeholder', 'Search by code or name'),
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'isActive',
          label: this.t('Status', 'Status'),
          placeholder: this.t('Select_status', 'Select status'),
          options: [
            { label: this.t('ExpenseConcept_Active', 'Active'), value: 'true' },
            { label: this.t('ExpenseConcept_Inactive', 'Inactive'), value: 'false' },
          ],
          showClear: true,
          defaultVisible: true,
        },
      ],
    };
  }

  private searchWithFilters(filters: ExpenseConceptFilters): void {
    const search = (filters.search ?? '').toString().trim();
    const isActive = _.isNil(filters.isActive) || filters.isActive === '' ? null : filters.isActive === 'true';
    this.currentSearchCriteria = { search: search || null, isActive };
    this.currentPage.set(1);
    this.search(this.currentSearchCriteria);
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.search(this.currentSearchCriteria);
  }

  onSort(event: SortEvent): void {
    const newField = event.field ?? null;
    const newOrder = event.order ?? 1;
    if (newField === this.sortField() && newOrder === this.sortOrder()) return;
    this.sortField.set(newField);
    this.sortOrder.set(newOrder);
    this.currentPage.set(1);
    this.search(this.currentSearchCriteria);
  }

  private search(criteria: ExpenseConceptSearchCommand): void {
    this.startLoading();
    this.proxy
      .search(criteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.items.set(response.data?.items ?? []);
          this.totalRecords.set(response.data?.totalCount ?? 0);
        },
        error: () => {
          this.items.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  localizedName(concept: ExpenseConcept): string {
    return this.locale.getLocalizedValue(concept.name) || concept.code;
  }

  openCreate(): void {
    this.openModal(null);
  }

  openEdit(concept: ExpenseConcept): void {
    this.openModal(concept);
  }

  /** The modal saves itself, so an API rejection keeps it open; a save comes back here only to refresh. */
  private openModal(concept: ExpenseConcept | null): void {
    this.modalService.open(ExpenseConceptModalComponent, { modalClass: 'modal_small' }, { concept }).subscribe((result) => {
      if (result?.action === 'save' && result.data) {
        this.search(this.currentSearchCriteria);
      }
    });
  }

  getRow(rowData: unknown): ExpenseConcept {
    return rowData as ExpenseConcept;
  }
}
